import type { PostHogInstance } from "@/types/posthog";

export const PREVIEW_TELEMETRY_STORAGE_KEY = "__AIDECO_PREVIEW_TELEMETRY__";

const OBSERVABLE_EVENT_NAMES = new Set([
  "first_render_started",
  "first_render_completed",
  "first_render_failed",
  "generation_start",
  "generation_response",
  "generation_complete",
  "generation_error",
]);

export type PreviewTelemetryEvent = {
  name: string;
  payload?: Record<string, unknown>;
  capturedAt: string;
};

type PreviewWindow = Window & {
  __AIDECO_PREVIEW_TELEMETRY__?: PreviewTelemetryEvent[];
};

type CaptureFn = PostHogInstance["capture"];
type PreviewLogger = Pick<Console, "info">;

const MAX_PREVIEW_EVENTS = 25;

const isPreviewHost = (hostname?: string) => hostname === "preview.aideco.us";

const isObservableEvent = (name: string) => OBSERVABLE_EVENT_NAMES.has(name);

const appendPreviewEvent = (
  name: string,
  payload: Record<string, unknown> | undefined,
  previewWindow: PreviewWindow,
  logger: PreviewLogger,
) => {
  const event: PreviewTelemetryEvent = {
    name,
    payload,
    capturedAt: new Date().toISOString(),
  };
  const nextEvents = [...(previewWindow.__AIDECO_PREVIEW_TELEMETRY__ ?? []), event].slice(
    -MAX_PREVIEW_EVENTS,
  );

  previewWindow.__AIDECO_PREVIEW_TELEMETRY__ = nextEvents;

  try {
    previewWindow.sessionStorage.setItem(
      PREVIEW_TELEMETRY_STORAGE_KEY,
      JSON.stringify(nextEvents),
    );
  } catch {
    // Ignore preview-storage failures; telemetry should stay best-effort.
  }

  logger.info("[aideco-preview-telemetry]", event);
};

export function recordPreviewTelemetryEvent(
  name: string,
  payload?: Record<string, unknown>,
  options: {
    previewWindow?: PreviewWindow;
    logger?: PreviewLogger;
  } = {},
): void {
  const previewWindow = options.previewWindow ?? (typeof window !== "undefined" ? window : undefined);
  if (!previewWindow || !isPreviewHost(previewWindow.location.hostname) || !isObservableEvent(name)) {
    return;
  }

  appendPreviewEvent(name, payload, previewWindow, options.logger ?? console);
}

export function installPreviewTelemetryObserver(
  posthog: Pick<PostHogInstance, "capture">,
  options: {
    previewWindow?: PreviewWindow;
    logger?: PreviewLogger;
  } = {},
): void {
  const previewWindow = options.previewWindow ?? (typeof window !== "undefined" ? window : undefined);
  if (!previewWindow || !isPreviewHost(previewWindow.location.hostname)) {
    return;
  }

  const currentCapture = posthog.capture as CaptureFn & { __aidecoPreviewWrapped?: boolean };
  if (currentCapture.__aidecoPreviewWrapped) {
    return;
  }

  const logger = options.logger ?? console;

  const wrappedCapture: CaptureFn & { __aidecoPreviewWrapped?: boolean } = ((...args: unknown[]) => {
    const [name, payload] = args as [string, Record<string, unknown> | undefined];

    if (isObservableEvent(name)) {
      appendPreviewEvent(name, payload, previewWindow, logger);
    }

    return currentCapture.apply(posthog, args as Parameters<CaptureFn>);
  }) as CaptureFn & { __aidecoPreviewWrapped?: boolean };

  wrappedCapture.__aidecoPreviewWrapped = true;
  posthog.capture = wrappedCapture;
}
